import {Component, Vue, Prop} from 'vue-property-decorator';
import {AxiosError, AxiosResponse} from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import {EnglishRules, Rules, validationRules} from '@/support/ValidationRules';
import MiButton from '@/components/mi-button/MiButton';
import {Attribute} from '@/models/Attribute';
import {AttributeOption} from '@/models/AttributeOption';

@Component<CreateDialog>({
  components: {
    MiButton,
  },
})
export default class CreateDialog extends Vue {
  @Prop({default: null})
  protected attribute!: Attribute;

  protected newAttribute: Attribute = new Attribute();
  protected isLoading: boolean = true;
  protected valid: boolean = false;
  protected rules: Rules = EnglishRules;

  protected types: Type[] = [
    {
      name: 'Text',
      value: 'text'
    },
    {
      name: 'Select',
      value: 'select'
    },
  ];

  mounted() {
    if (this.attribute) {
      this.newAttribute = this.attribute.clone();
      if (this.attribute.type === 'select') {
        this.fetchAttributeOptions();
      }
    } else {
      this.$set(this.newAttribute, 'options', [new AttributeOption()]);
      this.newAttribute.type = 'select';
    }
  }

  private getTranslation(key: string) {
    return this.$t(key);
  }

  protected fetchAttributeOptions() {
    new Attribute()
      .include('options')
      .find(this.newAttribute.id)
      .then((attribute: Attribute) => {
        this.newAttribute = attribute;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected close() {
    this.$emit('input', false);
    this.clear();
  }

  protected createOrUpdate() {
    if (this.newAttribute.id) {
      this.update();
    } else {
      this.create();
    }
  }

  protected update() {
    this.isLoading = true;
    if (!this.newAttribute) {
      return;
    }

    // if(this.newAttribute.type === 'text' && this.newAttribute.value)

    this.newAttribute
      .update()
      .then((attribute: Attribute) => {
        this.updateAttributeOptions(attribute)
          .catch((error: AxiosError) => {
            ErrorHandler.network(error);
          })
          .finally(() => {
          this.isLoading = false;
          this.$emit('attributeUpdated');
          this.close();
        });
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected updateAttributeOptions(attribute: Attribute) {
    if (!attribute || !attribute.id || !this.newAttribute.options) {
      return new Promise(() => {
      });
    }

    let options: any = [];
    this.newAttribute.options.forEach((attributeOption: AttributeOption) => {
      options.push(attributeOption.title);
    });

    let payload = {
      options: options,
    };

    return new Attribute(attribute).updateAttributeOption(attribute.id, payload);
  }

  protected create() {
    if (!this.canSaveAttribute) {
      return;
    }

    this.isLoading = true;
    this.newAttribute
      .create()
      .then((attribute: Attribute) => {
        if (this.newAttribute.type === 'select') {
          this.createAttributeOptions(attribute);
        } else {
          this.createAttributeValue(attribute);
        }

        this.isLoading = false;
        this.$emit('attributeUpdated');
        this.close();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected createAttributeValue(attribute: Attribute) {
    if (!attribute || !attribute.id || !this.newAttribute.value) {
      return
    }

    let payload = {
      value: this.newAttribute.value,
      attribute: attribute.id,
    };

    new Attribute(attribute)
      .create(payload)
      .then((response: AxiosResponse) => {
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected createAttributeOptions(attribute: Attribute) {
    if (!this.newAttribute.options) {
      return
    }

    let count = 0;
    this.newAttribute.options.forEach((attributeOption: AttributeOption) => {
      attributeOption.attribute = attribute.id;

      attributeOption
        .create()
        .then((attributeOption: AttributeOption) => {
          // if (!this.newAttribute.options) {
          //   return;
          // }

          // count += 1;
          // if (count === this.newAttribute.options.length) {
          //   return;
          // }
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });
    });
  }

  protected createAttributeOption() {
    new AttributeOption()
      .create({
        attribute: this.newAttribute.id,
        title: '',
      })
      .then((attributeOption: AttributeOption) => {
        this.fetchAttributeOptions();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected canDeleteAttributeOption(index: number, option: AttributeOption) {
    if (this.attribute && option && option.id) {
      new AttributeOption(option)
        .delete()
        .then((attributeOption: AttributeOption) => {
          this.fetchAttributeOptions();
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });
    } else if (this.newAttribute && this.newAttribute.options) {
      this.newAttribute.options.splice(index, 1);
    }
  }

  protected get canSaveAttribute() {
    let canSave: boolean = true
    if (!this.newAttribute || !this.newAttribute.title || !this.newAttribute.title.length) {
      canSave = false;
    }

    if (!this.newAttribute.type || !this.newAttribute.type.length) {
      canSave = false;
    }

    if (this.newAttribute.type === 'select') {
      if (this.newAttribute.options) {
        this.newAttribute.options.forEach((attributeOption: AttributeOption) => {
          if (!attributeOption.title) {
            canSave = false;
          }
        });
      }
    }

    return canSave;
  }

  protected addSelectOption() {
    if (!this.newAttribute.options) {
      this.$set(this.newAttribute, 'options', [new AttributeOption()]);
    } else {
      this.newAttribute.options.push(new AttributeOption());
    }
  }

  protected get saveButtonText() {
    if (this.newAttribute && this.newAttribute.id) {
      return 'Edit';
    }

    return 'Add';
  }

  protected get dialogHeader() {
    if (this.newAttribute && this.newAttribute.id) {
      return `Edit ${this.newAttribute.title}`;
    }
    return 'Create attribute';
  }

  protected clear() {
    this.newAttribute = new Attribute();
  }
}


interface Type {
  [key: string]: string;
}
